import { useState, useEffect } from "react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import api from "../../lib/api";
import { Link2, Copy, Check, Mail, Pencil, X, History, FileText, DollarSign, AlertTriangle, Printer, Cake, Gift, Palette, Sparkles, ChevronDown, ChevronUp, Clock, Phone } from "lucide-react";
import LoadMore from "../../components/LoadMore";
import { DunkinAddons } from "../../components/DunkinAddons";

const STATUS_META = {
  new: { label: "New Inquiry", cls: "bg-[#E0218A] text-white" },
  contacted: { label: "Contacted", cls: "bg-[#C6FF33] text-[#121212]" },
  confirmed: { label: "Deposit Paid / Confirmed", cls: "bg-[#121212] text-[#C6FF33]" },
  awaiting_final_details: { label: "Awaiting Final Details", cls: "bg-[#F5A623] text-[#121212]" },
  awaiting_final_payment: { label: "Awaiting Final Payment", cls: "bg-[#B4472F] text-white" },
  finalized: { label: "Finalized", cls: "bg-emerald-600 text-white" },
  completed: { label: "Completed", cls: "bg-emerald-700 text-white" },
  closed: { label: "Closed", cls: "bg-stone-100 text-stone-600" },
};
const STATUS_OPTIONS = [
  { v: "new", l: "New Inquiry" },
  { v: "contacted", l: "Contacted" },
  { v: "confirmed", l: "Deposit Paid / Confirmed" },
  { v: "closed", l: "Closed" },
];
const PACKAGES = ["The Mini Society", "The Society Party", "The Signature Society Party"];
const ADDON_CATALOG = [
  { key: "matching_cake", label: "8-inch Matching Celebration Cake", price_label: "$85" },
  { key: "favor_bag", label: "Cake Society Favor Bag", price_label: "$12 per decorating guest" },
  { key: "party_banner", label: "Custom Painted Party Banner by Water Lily Creative Co.", price_label: "$70" },
];

const inp = "w-full px-3 py-2 border-2 border-[#121212] bg-white focus:outline-none text-sm";
const lbl = "text-[11px] tracking-[0.15em] uppercase font-bold text-[#121212]/70 block mb-1";

function esc(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function openRunSheet(p) {
  const fd = p.final_details || {};
  const guestCount = fd.final_guest_count ?? p.num_guests ?? "—";
  const addons = (fd.addons && fd.addons.length ? fd.addons : (p.deposit_addons || []));
  const themeNotes = fd.theme_notes || p.theme_colors || "";
  const cakeNotes = fd.cake_notes || "";
  const bannerNotes = fd.banner_notes || "";
  const allergies = fd.allergies || "";
  const special = fd.special_requests || p.special_requests || "";

  const hasCake = addons.some((a) => a.key === "matching_cake");
  const hasFavor = addons.some((a) => a.key === "favor_bag");
  const hasBanner = addons.some((a) => a.key === "party_banner");

  // Dunkin' catering order (final selection if submitted, else deposit-time)
  const dunkin = (fd.dunkin_addons && fd.dunkin_addons.length ? fd.dunkin_addons : (p.dunkin_addons || []));

  const addonRow = (on, label, detail) =>
    `<div class="chk ${on ? "on" : ""}"><span class="box">${on ? "&#10003;" : ""}</span><div><div class="chk-label">${esc(label)}</div>${detail ? `<div class="chk-detail">${esc(detail)}</div>` : ""}</div></div>`;

  const section = (title, body) =>
    body ? `<div class="sec"><div class="sec-h">${esc(title)}</div><div class="sec-b">${body}</div></div>` : "";

  const favorCount = Number(fd.final_guest_count ?? p.num_guests ?? 0) || 0;

  const dunkinRows = dunkin.map((d) => {
    const label = String(d.label || d.key || "").replace(/^Dunkin' /, "");
    const qty = Number(d.qty || 0);
    const variant = d.variant ? ` — ${d.variant}` : "";
    return addonRow(true, `${qty}× ${label}${variant}`, "");
  }).join("");
  const dunkinSection = dunkin.length
    ? `<div class="sec dunkin"><div class="sec-h">&#9749; Dunkin' Catering</div><div class="sec-b">${dunkinRows}</div></div>`
    : "";

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Party Run-Sheet — ${esc(p.name || "")}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Outfit:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: letter; margin: 0.5in; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Outfit', system-ui, sans-serif; color: #121212; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet { max-width: 7.5in; margin: 0 auto; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #121212; padding-bottom: 10px; }
  .logo { height: 46px; width: auto; display: block; margin-bottom: 6px; }
  .brand { font-size: 11px; letter-spacing: 0.25em; text-transform: uppercase; font-weight: 700; color: #E0218A; }
  .title { font-family: 'Cormorant Garamond', serif; font-size: 34px; font-weight: 700; line-height: 1; margin-top: 2px; }
  .datebadge { text-align: right; }
  .datebadge .d { font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 700; }
  .datebadge .t { font-size: 12px; font-weight: 600; color: #E0218A; text-transform: uppercase; letter-spacing: 0.1em; }
  .pkg { display: inline-block; margin-top: 4px; font-size: 10px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; background: #121212; color: #C6FF33; padding: 3px 8px; }
  .hero { background: #F5EFE6; border: 2px solid #121212; margin-top: 14px; display: grid; grid-template-columns: 1fr 1fr 1fr; }
  .hero > div { padding: 12px 14px; border-right: 2px solid #121212; }
  .hero > div:last-child { border-right: none; }
  .hero .k { font-size: 9px; letter-spacing: 0.15em; text-transform: uppercase; font-weight: 700; color: #121212; opacity: 0.6; }
  .hero .v { font-family: 'Cormorant Garamond', serif; font-size: 26px; font-weight: 700; line-height: 1.05; margin-top: 2px; }
  .hero .v small { font-family: 'Outfit'; font-size: 12px; font-weight: 600; opacity: 0.7; }
  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }
  .sec { margin-bottom: 12px; break-inside: avoid; }
  .sec-h { font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 700; color: #E0218A; border-bottom: 1.5px solid #121212; padding-bottom: 3px; margin-bottom: 6px; }
  .sec-b { font-size: 13px; line-height: 1.5; color: #292524; white-space: pre-wrap; }
  .chk { display: flex; gap: 8px; align-items: flex-start; padding: 5px 0; }
  .chk .box { width: 16px; height: 16px; border: 2px solid #121212; flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; margin-top: 1px; }
  .chk.on .box { background: #C6FF33; }
  .chk.on .chk-label { font-weight: 700; }
  .chk:not(.on) { opacity: 0.4; }
  .chk-label { font-size: 13px; }
  .chk-detail { font-size: 11px; color: #78716C; }
  .allergy { background: #F7E9E4; border: 2px solid #B4472F; padding: 10px 12px; }
  .allergy .sec-h { color: #B4472F; border-color: #B4472F; }
  .allergy .sec-b { font-weight: 600; }
  .dunkin { background: #FFF3E9; border: 2px solid #FF6E0C; padding: 10px 12px; }
  .dunkin .sec-h { color: #FF6E0C; border-color: #FF6E0C; }
  .dunkin .chk.on .box { background: #FF6E0C; color: #fff; }
  .contact { margin-top: 16px; border-top: 2px solid #121212; padding-top: 8px; display: flex; justify-content: space-between; font-size: 12px; }
  .contact b { font-family: 'Cormorant Garamond', serif; font-size: 16px; }
  .foot { margin-top: 10px; text-align: center; font-size: 9px; letter-spacing: 0.15em; text-transform: uppercase; color: #78716C; }
  @media print { .noprint { display: none !important; } }
  .noprint { position: fixed; top: 12px; right: 12px; }
  .noprint button { font-family: 'Outfit'; background: #E0218A; color: #fff; border: none; padding: 10px 18px; font-weight: 700; font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; }
</style></head><body>
<div class="noprint"><button onclick="window.print()">Print / Save PDF</button></div>
<div class="sheet">
  <div class="head">
    <div>
      <img class="logo" src="{{LOGO_SRC}}" alt="Cake Society" />
      <div class="brand">Party Prep Run-Sheet</div>
      <div class="title">${esc(p.name || "Party")}${p.occasion ? ` <span style="font-family:'Outfit';font-size:14px;font-weight:500;opacity:0.6">· ${esc(p.occasion)}</span>` : ""}</div>
      <div class="pkg">${esc(p.package || "Party Package")}</div>
    </div>
    <div class="datebadge">
      <div class="d">${esc(p.desired_date || "Date TBD")}</div>
      <div class="t">${esc(p.preferred_time || "")}</div>
    </div>
  </div>

  <div class="hero">
    <div><div class="k">Decorating Guests</div><div class="v">${esc(guestCount)}</div></div>
    <div><div class="k">Guest of Honor</div><div class="v">${esc(p.child_name || "—")}${p.child_age ? ` <small>turning ${esc(p.child_age)}</small>` : ""}</div></div>
    <div><div class="k">Package</div><div class="v" style="font-size:18px">${esc(p.package || "—")}</div></div>
  </div>

  <div class="cols">
    <div>
      <div class="sec">
        <div class="sec-h">Add-Ons</div>
        <div class="sec-b">
          ${addonRow(hasCake, "8-inch Matching Celebration Cake", "")}
          ${addonRow(hasFavor, "Cake Society Favor Bags", favorCount ? `${favorCount} bags` : "")}
          ${addonRow(hasBanner, "Custom Painted Party Banner (Water Lily Creative Co.)", "")}
        </div>
      </div>
      ${section("Theme / Colors", esc(themeNotes))}
      ${section("Cake Design Notes", esc(cakeNotes))}
      ${section("Banner Design Notes", esc(bannerNotes))}
    </div>
    <div>
      ${dunkinSection}
      <div class="sec allergy">
        <div class="sec-h">⚠ Allergies / Dietary</div>
        <div class="sec-b">${allergies ? esc(allergies) : "None reported"}</div>
      </div>
      ${section("Special Requests", esc(special))}
      <div class="sec">
        <div class="sec-h">Day-of Notes</div>
        <div class="sec-b" style="min-height:90px; border:1.5px dashed #121212; opacity:0.5"></div>
      </div>
    </div>
  </div>

  <div class="contact">
    <div><div style="font-size:9px;letter-spacing:0.15em;text-transform:uppercase;font-weight:700;opacity:0.6">Booked by</div><b>${esc(p.name || "—")}</b></div>
    <div style="text-align:right"><div style="font-size:9px;letter-spacing:0.15em;text-transform:uppercase;font-weight:700;opacity:0.6">Contact</div><b>${esc(p.phone || "—")}</b><div style="font-size:11px;opacity:0.7">${esc(p.email || "")}</div></div>
  </div>
  <div class="foot">Cake Society Studio · Printed ${new Date().toLocaleDateString()}</div>
</div>
</body></html>`;

  const w = window.open("", "_blank");
  if (!w) { toast.error("Please allow pop-ups to print the run-sheet."); return; }
  w.document.open();
  w.document.write(html.replace("{{LOGO_SRC}}", `${window.location.origin}/cake-society-logo.png`));
  w.document.close();
}

function EditForm({ p, editParty, onClose }) {
  const [f, setF] = useState({
    name: p.name || "", email: p.email || "", phone: p.phone || "",
    desired_date: p.desired_date || "", preferred_time: p.preferred_time || "",
    package: p.package || "", child_name: p.child_name || "", child_age: p.child_age || "",
    theme_colors: p.theme_colors || "", num_guests: p.num_guests || "",
    special_requests: p.special_requests || "",
    deposit_amount: p.deposit_amount ?? p.deposit_link_amount ?? 100,
    deposit_paid: !!p.deposit_paid,
    discount_type: p.discount_type || "", discount_value: p.discount_value ?? "",
    status: p.status || "new",
    addon_keys: (p.final_details_submitted_at ? (p.final_details?.addons || []) : (p.deposit_addons || [])).map((a) => a.key),
    manual_charges: (p.manual_charges || []).map((m) => ({ ...m })),
    dunkin_addons: ((p.final_details_submitted_at ? p.final_details?.dunkin_addons : p.dunkin_addons) || []).map((d) => ({ key: d.key, qty: d.qty, variant: d.variant || "" })),
    final_theme_notes: p.final_details?.theme_notes || "",
    final_cake_notes: p.final_details?.cake_notes || "",
    final_banner_notes: p.final_details?.banner_notes || "",
    final_allergies: p.final_details?.allergies || "",
    final_special_requests: p.final_details?.special_requests || "",
    date_options: (p.date_options || []).map((o) => ({ date: o.date || "", time: o.time || "" })),
  });
  const PARTY_TIME_SLOTS = ["10:00 AM–12:00 PM", "2:00–4:00 PM", "5:30–7:30 PM"];
  const finalSubmitted = !!p.final_details_submitted_at;
  const [dunkinCatalog, setDunkinCatalog] = useState([]);
  useEffect(() => {
    api.get("/admin/party-dunkin-catalog").then(({ data }) => setDunkinCatalog(data)).catch(() => {});
  }, []);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const toggleAddon = (key) => set("addon_keys", f.addon_keys.includes(key) ? f.addon_keys.filter((k) => k !== key) : [...f.addon_keys, key]);
  const setCharge = (i, field, val) => set("manual_charges", f.manual_charges.map((m, j) => j === i ? { ...m, [field]: val } : m));
  const addCharge = () => set("manual_charges", [...f.manual_charges, { label: "", amount: "" }]);
  const rmCharge = (i) => set("manual_charges", f.manual_charges.filter((_, j) => j !== i));
  const setOpt = (i, field, val) => set("date_options", f.date_options.map((o, j) => j === i ? { ...o, [field]: val } : o));
  const addOpt = () => set("date_options", [...f.date_options, { date: "", time: PARTY_TIME_SLOTS[1] }]);
  const rmOpt = (i) => set("date_options", f.date_options.filter((_, j) => j !== i));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        deposit_amount: f.deposit_amount === "" ? null : Number(f.deposit_amount),
        discount_value: f.discount_value === "" ? null : Number(f.discount_value),
        manual_charges: f.manual_charges.map((m) => ({ label: m.label, amount: m.amount === "" ? 0 : Number(m.amount) })),
      };
      // Only send final-details overrides once the customer has actually submitted.
      if (!finalSubmitted) {
        delete payload.final_theme_notes; delete payload.final_cake_notes;
        delete payload.final_banner_notes; delete payload.final_allergies;
        delete payload.final_special_requests;
      }
      const res = await editParty(p.id, payload);
      toast.success(res.changed ? "Party booking updated" : "No changes to save");
      if (res.final_link_stale) toast.warning("Balance changed — the existing final payment link may be inaccurate. Regenerate & resend it.", { duration: 7000 });
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`edit-party-form-${p.id}`} className="mt-4 pt-4 border-t-2 border-dashed border-[#121212]/30 space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Customer name</label><input data-testid="edit-name" className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div><label className={lbl}>Customer email</label><input data-testid="edit-email" className={inp} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
        <div><label className={lbl}>Customer phone</label><input data-testid="edit-phone" className={inp} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
        <div><label className={lbl}>Party package</label>
          <select data-testid="edit-package" className={inp} value={f.package} onChange={(e) => set("package", e.target.value)}>
            <option value="">— select —</option>
            {PACKAGES.map((pk) => <option key={pk} value={pk}>{pk}</option>)}
          </select>
        </div>
        <div><label className={lbl}>Party date</label><input data-testid="edit-date" type="date" className={inp} value={f.desired_date} onChange={(e) => set("desired_date", e.target.value)} /></div>
        <div><label className={lbl}>Party start time</label><input data-testid="edit-time" className={inp} placeholder="e.g. 2:00–4:00 PM" value={f.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} /></div>
        <div><label className={lbl}>Birthday child's name</label><input data-testid="edit-child-name" className={inp} value={f.child_name} onChange={(e) => set("child_name", e.target.value)} /></div>
        <div><label className={lbl}>Birthday child's age</label><input data-testid="edit-child-age" className={inp} value={f.child_age} onChange={(e) => set("child_age", e.target.value)} /></div>
        <div><label className={lbl}>Theme</label><input data-testid="edit-theme" className={inp} value={f.theme_colors} onChange={(e) => set("theme_colors", e.target.value)} /></div>
        <div><label className={lbl}>Number of decorating guests</label><input data-testid="edit-guests" type="number" min="1" className={inp} value={f.num_guests} onChange={(e) => set("num_guests", e.target.value)} /></div>
      </div>

      {/* Alternate date options for the customer to choose on the deposit page */}
      <div data-testid="date-options-editor" className="border-2 border-dashed border-[#E0218A]/50 bg-[#E0218A]/5 p-4">
        <label className={lbl}>Offer alternate dates <span className="normal-case tracking-normal text-[#121212]/55 font-normal">· if their requested date isn't available</span></label>
        <p className="text-[11px] text-[#121212]/60 mb-3">Add a few open date + time options. When set, the customer's deposit page shows these as choices and they pick one before paying — their pick becomes the party's date.</p>
        <div className="space-y-2">
          {f.date_options.map((o, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <input data-testid={`opt-date-${i}`} type="date" className={inp + " flex-1 min-w-[150px]"} value={o.date} onChange={(e) => setOpt(i, "date", e.target.value)} />
              <select data-testid={`opt-time-${i}`} className={inp + " flex-1 min-w-[150px]"} value={o.time} onChange={(e) => setOpt(i, "time", e.target.value)}>
                {PARTY_TIME_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button type="button" data-testid={`opt-remove-${i}`} onClick={() => rmOpt(i)} className="p-2 text-[#121212]/50 hover:text-red-600"><X size={16} /></button>
            </div>
          ))}
        </div>
        <button type="button" data-testid="opt-add" onClick={addOpt} className="mt-2 text-xs tracking-[0.15em] uppercase font-bold text-[#E0218A] hover:underline">+ Add date option</button>
      </div>

      <div>
        <label className={lbl}>Add-ons {finalSubmitted && <span className="text-[#E0218A] normal-case tracking-normal font-bold">· editing the final billed add-ons</span>}</label>
        <div className="flex flex-wrap gap-2">
          {ADDON_CATALOG.map((a) => {
            const on = f.addon_keys.includes(a.key);
            return (
              <button key={a.key} type="button" data-testid={`edit-addon-${a.key}`} onClick={() => toggleAddon(a.key)}
                className={`px-3 py-2 text-xs font-bold border-2 transition-all ${on ? "border-[#E0218A] bg-[#E0218A]/10 text-[#121212]" : "border-stone-300 text-stone-500 hover:border-[#121212]"}`}>
                {on ? "✓ " : ""}{a.label} ({a.price_label})
              </button>
            );
          })}
        </div>
        {finalSubmitted && <p className="mt-2 text-[11px] text-[#121212]/60">The customer submitted their final details — these are the add-ons that will be billed. Toggle any add-on back on/off to override their selection, then regenerate the payment link.</p>}
      </div>

      {dunkinCatalog.length > 0 && (
        <div>
          <label className={lbl}>Dunkin' catering {finalSubmitted && <span className="text-[#FF6E0C] normal-case tracking-normal font-bold">· editing the final billed order</span>}</label>
          <DunkinAddons catalog={dunkinCatalog} value={f.dunkin_addons} onChange={(v) => set("dunkin_addons", v)} showCost />
        </div>
      )}

      {finalSubmitted && (
        <div data-testid={`fd-override-${p.id}`} className="bg-[#F5EFE6] border-2 border-[#121212] p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-[#E0218A]" />
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]">Final Details — Override</p>
          </div>
          <p className="text-[11px] text-[#121212]/60 -mt-1">Correct anything the customer submitted before sending their final payment link. The decorating guest count and add-ons above also feed the final balance.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={lbl}>Theme / colors</label><input data-testid="edit-final-theme" className={inp} value={f.final_theme_notes} onChange={(e) => set("final_theme_notes", e.target.value)} /></div>
            <div><label className={lbl}>Cake notes</label><input data-testid="edit-final-cake" className={inp} value={f.final_cake_notes} onChange={(e) => set("final_cake_notes", e.target.value)} /></div>
            <div><label className={lbl}>Banner notes</label><input data-testid="edit-final-banner" className={inp} value={f.final_banner_notes} onChange={(e) => set("final_banner_notes", e.target.value)} /></div>
            <div><label className={lbl}>Allergies / dietary</label><input data-testid="edit-final-allergies" className={inp} value={f.final_allergies} onChange={(e) => set("final_allergies", e.target.value)} /></div>
          </div>
          <div><label className={lbl}>Special requests</label><textarea data-testid="edit-final-special" rows={2} className={inp + " resize-none"} value={f.final_special_requests} onChange={(e) => set("final_special_requests", e.target.value)} /></div>
        </div>
      )}

      <div><label className={lbl}>Notes / special requests</label><textarea data-testid="edit-notes" rows={2} className={inp + " resize-none"} value={f.special_requests} onChange={(e) => set("special_requests", e.target.value)} /></div>

      <div>
        <label className={lbl}>Other manual charges</label>
        <div className="space-y-2">
          {f.manual_charges.map((m, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input data-testid={`edit-charge-label-${i}`} placeholder="Description (e.g. Extra time)" className={inp + " flex-1"} value={m.label} onChange={(e) => setCharge(i, "label", e.target.value)} />
              <div className="flex items-center border-2 border-[#121212]"><span className="px-2 text-stone-500">$</span><input data-testid={`edit-charge-amount-${i}`} type="number" step="0.01" className="w-24 px-2 py-2 focus:outline-none" value={m.amount} onChange={(e) => setCharge(i, "amount", e.target.value)} /></div>
              <button type="button" onClick={() => rmCharge(i)} className="p-2 text-stone-400 hover:text-red-500"><X size={16} /></button>
            </div>
          ))}
        </div>
        <button type="button" data-testid="edit-add-charge" onClick={addCharge} className="mt-2 text-xs tracking-[0.15em] uppercase font-bold text-[#E0218A]">+ Add charge</button>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 items-end bg-[#F5EFE6] p-3 border-2 border-[#121212]">
        <div><label className={lbl}>Deposit amount ($)</label><input data-testid="edit-deposit-amount" type="number" min="0" step="0.01" className={inp} value={f.deposit_amount} onChange={(e) => set("deposit_amount", e.target.value)} /></div>
        <label className="flex items-center gap-2 text-sm font-bold cursor-pointer pb-2" data-testid="edit-deposit-paid">
          <input type="checkbox" className="w-4 h-4 accent-[#121212]" checked={f.deposit_paid} onChange={(e) => set("deposit_paid", e.target.checked)} /> Deposit paid
        </label>
        <div><label className={lbl}>Discount</label>
          <select data-testid="edit-discount-type" className={inp} value={f.discount_type} onChange={(e) => set("discount_type", e.target.value)}>
            <option value="">None</option>
            <option value="amount">$ amount</option>
            <option value="percent">% percent</option>
          </select>
        </div>
        <div><label className={lbl}>Discount value</label><input data-testid="edit-discount-value" type="number" min="0" step="0.01" className={inp} value={f.discount_value} onChange={(e) => set("discount_value", e.target.value)} disabled={!f.discount_type} /></div>
      </div>

      <div>
        <label className={lbl}>Party status</label>
        <select data-testid="edit-status" className={inp + " sm:w-64"} value={f.status} onChange={(e) => set("status", e.target.value)}>
          {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </div>

      <div className="flex gap-2">
        <button data-testid={`save-party-${p.id}`} onClick={save} disabled={busy} className="px-6 py-2.5 bg-[#121212] text-[#F2E9D8] border-2 border-[#121212] hover:bg-black disabled:opacity-50 text-xs tracking-[0.2em] uppercase font-bold">{busy ? "Saving…" : "Save changes"}</button>
        <button data-testid={`cancel-party-${p.id}`} onClick={onClose} className="px-6 py-2.5 border-2 border-[#121212] hover:bg-stone-100 text-xs tracking-[0.2em] uppercase font-bold">Cancel</button>
      </div>
    </div>
  );
}

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

function FinalBalance({ p, getPartyBalance, createFinalPaymentLink, markFinalPaid, reopenParty }) {
  const [bd, setBd] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [link, setLink] = useState(p.final_link_url || "");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try { setBd(await getPartyBalance(p.id)); } catch (e) { toast.error("Couldn't load balance"); }
  };
  const openPanel = async () => { setOpen((v) => !v); if (!bd) await load(); };

  const genLink = async (send) => {
    setBusy(send ? "email" : "gen");
    try {
      const data = await createFinalPaymentLink(p.id, send);
      setLink(data.url); setBd(data.breakdown);
      toast.success(send ? (data.emailed ? `Final payment link emailed to ${p.email}` : "Link created (no email on file)") : "Final payment link created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const markPaid = async () => {
    setBusy("paid");
    try { await markFinalPaid(p.id); toast.success("Final balance marked paid — party finalized"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const reopen = async () => {
    setBusy("reopen");
    try { await reopenParty(p.id); toast.success("Booking reopened for edits"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="mt-4 pt-4 border-t border-stone-200">
      <button data-testid={`final-balance-toggle-${p.id}`} onClick={openPanel} className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]/70 inline-flex items-center gap-1.5">
        <DollarSign size={13} /> Final balance & payment {open ? "▲" : "▼"}
      </button>
      {p.final_link_stale && (
        <p data-testid={`stale-warning-${p.id}`} className="mt-2 text-xs font-bold text-[#B4472F] bg-[#F7E9E4] border border-[#B4472F] px-3 py-2 rounded inline-flex items-center gap-1.5">
          <AlertTriangle size={13} /> The booking changed — the existing payment link may be inaccurate. Regenerate & resend it.
        </p>
      )}
      {open && (
        <div data-testid={`final-balance-panel-${p.id}`} className="mt-3">
          {!bd ? <p className="text-sm text-stone-400">Loading…</p> : (
            <div className="bg-[#F5EFE6] border-2 border-[#121212] p-4 max-w-md text-sm">
              <div className="flex justify-between"><span>{bd.package || "Package"}</span><span>{money(bd.base)}</span></div>
              {bd.extra_seats > 0 && <div className="flex justify-between text-stone-600"><span>Extra guests ({bd.extra_seats} × {money(bd.extra_seat_fee)})</span><span>{money(bd.extra_guest_total)}</span></div>}
              {bd.addons.map((a, i) => <div key={i} className="flex justify-between text-stone-600"><span>{a.label} ({a.detail})</span><span>{money(a.amount)}</span></div>)}
              {bd.manual_charges.map((m, i) => <div key={i} className="flex justify-between text-stone-600"><span>{m.label}</span><span>{money(m.amount)}</span></div>)}
              {bd.discount_amount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount{bd.discount_type === "percent" ? ` (${bd.discount_value}%)` : ""}</span><span>−{money(bd.discount_amount)}</span></div>}
              <div className="flex justify-between font-bold border-t border-stone-400 mt-2 pt-2"><span>Total party cost</span><span>{money(bd.total)}</span></div>
              {bd.deposit_credit > 0 && <div className="flex justify-between text-stone-600"><span>Deposit paid</span><span>−{money(bd.deposit_credit)}</span></div>}
              <div className="flex justify-between font-black text-base border-t-2 border-[#121212] mt-2 pt-2"><span>Balance due</span><span data-testid={`balance-due-${p.id}`}>{money(bd.balance_due)}</span></div>
              {!bd.deposit_paid && bd.deposit_amount > 0 && <p className="text-[11px] text-stone-500 mt-1">Deposit not marked paid — not subtracted.</p>}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            <button data-testid={`gen-final-link-${p.id}`} onClick={() => genLink(false)} disabled={!!busy} className="px-3 py-1.5 text-xs tracking-[0.1em] uppercase font-bold bg-[#E0218A] text-white border-2 border-[#E0218A] hover:opacity-90 disabled:opacity-50">{busy === "gen" ? "…" : link ? "Regenerate link" : "Generate final payment link"}</button>
            <button data-testid={`email-final-link-${p.id}`} onClick={() => genLink(true)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.1em] uppercase font-bold bg-[#121212] text-[#F2E9D8] border-2 border-[#121212] hover:bg-black disabled:opacity-50"><Mail size={13} /> {busy === "email" ? "…" : "Email payment link"}</button>
            {!p.final_paid && <button data-testid={`mark-final-paid-${p.id}`} onClick={markPaid} disabled={!!busy} className="px-3 py-1.5 text-xs tracking-[0.1em] uppercase font-bold border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white disabled:opacity-50">Mark balance paid</button>}
            {(p.final_paid || p.status === "finalized") && <button data-testid={`reopen-${p.id}`} onClick={reopen} disabled={!!busy} className="px-3 py-1.5 text-xs tracking-[0.1em] uppercase font-bold border-2 border-[#121212] hover:bg-stone-100 disabled:opacity-50">Reopen for edits</button>}
          </div>
          {link && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <a data-testid={`final-link-${p.id}`} href={link} target="_blank" rel="noreferrer" className="text-sm underline break-all">{link}</a>
              <button onClick={copy} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold border border-[#121212] hover:bg-[#121212] hover:text-white">{copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function parseDate(s) {
  if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}
function longDate(s) {
  const dt = parseDate(s);
  if (!dt) return s || "";
  const mon = MONTHS[dt.getMonth()][0] + MONTHS[dt.getMonth()].slice(1).toLowerCase();
  return `${DOW[dt.getDay()]}, ${mon} ${dt.getDate()}`;
}
function ordinal(n) {
  const num = parseInt(n, 10);
  if (isNaN(num)) return `${n}`;
  const s = ["th", "st", "nd", "rd"], v = num % 100;
  return num + (s[(v - 20) % 10] || s[v] || s[0]);
}
function childHeadline(p) {
  if (p.child_name && p.child_age) return `${p.child_name}'s ${ordinal(p.child_age)} Birthday`;
  if (p.child_name) return `${p.child_name}'s Birthday`;
  return p.occasion || "Birthday Party";
}
function deriveStatus(p) {
  if (p.status === "completed") return { key: "completed", label: "Completed", cls: "bg-[#121212] text-white" };
  if (p.status === "closed") return { key: "closed", label: "Closed", cls: "bg-stone-200 text-stone-600" };
  if (p.final_paid || p.status === "finalized") return { key: "paid_full", label: "Paid in Full", cls: "bg-emerald-600 text-white" };
  if (p.status === "awaiting_final_payment") return { key: "final_due", label: "Final Payment Due", cls: "bg-[#B4472F] text-white" };
  if (p.status === "awaiting_final_details" || (p.final_details_sent_at && !p.final_details_submitted_at)) return { key: "final_details", label: "Awaiting Final Details", cls: "bg-[#E0218A] text-white" };
  if (p.deposit_paid || p.status === "confirmed") return { key: "deposit_paid", label: "Deposit Paid", cls: "bg-[#C6FF33] text-[#121212]" };
  return { key: "deposit_needed", label: "Deposit Needed", cls: "bg-white text-[#121212] border-2 border-[#121212]" };
}
const ATTENTION_KEYS = new Set(["deposit_needed", "final_details", "final_due"]);
function progressSteps(p) {
  const booked = p.deposit_paid || ["confirmed", "awaiting_final_details", "awaiting_final_payment", "finalized", "completed"].includes(p.status);
  const details = !!p.final_details_submitted_at || ["awaiting_final_payment", "finalized", "completed"].includes(p.status);
  const pay = p.final_paid || ["finalized", "completed"].includes(p.status);
  const day = p.status === "completed";
  return [
    { label: "Booked", done: booked },
    { label: "Details", done: details },
    { label: "Final Payment", done: pay },
    { label: "Party Day", done: day },
  ];
}
const ADDON_ICONS = { matching_cake: Cake, favor_bag: Gift, party_banner: Palette };
function addonList(p) {
  const raw = (p.final_details?.addons?.length ? p.final_details.addons : (p.deposit_addons || []));
  return raw.map((a) => ({
    key: a.key,
    Icon: ADDON_ICONS[a.key] || Sparkles,
    label: a.label,
    detail: a.key === "favor_bag" ? `${p.final_details?.final_guest_count ?? p.num_guests ?? "?"} guests` : (a.price_label || (a.price != null ? `$${a.price}` : "")),
  }));
}

function ProgressStepper({ p }) {
  const steps = progressSteps(p);
  const firstUndone = steps.findIndex((s) => !s.done);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-8" data-testid={`progress-${p.id}`}>
      {steps.map((s, i) => (
        <div key={s.label} className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${s.done ? "bg-[#121212]" : (firstUndone === i ? "bg-[#E0218A]" : "bg-[#121212]/15")}`} />
          <span className={`text-[11px] uppercase tracking-[0.15em] font-bold ${s.done || firstUndone === i ? "text-[#121212]" : "text-[#121212]/40"}`}>{s.label}</span>
          {i < steps.length - 1 && <span className="w-6 h-px bg-[#121212]/15" />}
        </div>
      ))}
    </div>
  );
}


function PartyCard({ p, setInquiryStatus, createDepositLink, editParty, sendFinalDetails, getPartyBalance, createFinalPaymentLink, markFinalPaid, reopenParty }) {
  const meta = deriveStatus(p);
  const addons = addonList(p);
  const dt = parseDate(p.desired_date);
  const [amount, setAmount] = useState(p.deposit_link_amount || 100);
  const [link, setLink] = useState(p.deposit_link_url || "");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [sendingFD, setSendingFD] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showPayTools, setShowPayTools] = useState(false);

  const sendFD = async () => {
    setSendingFD(true);
    try {
      const data = await sendFinalDetails(p.id);
      toast.success(data.emailed ? `Final details form emailed to ${p.email}` : "Form ready (no email on file)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendingFD(false); }
  };

  const generate = async (sendToCustomer = false) => {
    setBusy(true);
    try {
      const data = await createDepositLink(p.id, amount, sendToCustomer);
      setLink(data.url);
      toast.success(sendToCustomer ? (data.emailed ? `Deposit link emailed to ${p.email}` : "Link created (no email on file)") : "Deposit link created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const depAmt = Number(p.deposit_amount ?? p.deposit_link_amount ?? 0);
  const log = p.activity_log || [];
  const showPayment = p.deposit_paid || p.final_details_submitted_at || p.final_link_url || ["awaiting_final_payment", "finalized", "completed"].includes(p.status);
  const canRunSheet = p.final_details_submitted_at || ["awaiting_final_payment", "finalized", "completed"].includes(p.status);
  const canSendFD = p.deposit_paid || p.status === "confirmed" || p.final_details_sent_at;
  const showDepositTools = p.status !== "confirmed" && p.status !== "closed" && !p.deposit_paid;
  const primaryBtn = "inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-[0.15em] uppercase font-bold transition-colors disabled:opacity-50";

  return (
    <div className="bg-white border-b border-[#121212]/10 last:border-b-0">
      {/* ── Collapsed scannable header ── */}
      <div data-testid={`party-card-${p.id}`} onClick={() => setExpanded((v) => !v)} className="flex flex-col lg:flex-row gap-6 lg:gap-8 p-6 md:p-8 cursor-pointer hover:bg-[#F2E9D8]/50 transition-colors items-start lg:items-center">
        <div className="w-full lg:w-44 shrink-0">
          <p className="text-lg font-bold uppercase tracking-[0.2em] text-[#E0218A]">{dt ? MONTHS[dt.getMonth()] : "TBD"}</p>
          <p className="text-6xl font-black tracking-tighter text-[#121212] leading-none my-0.5">{dt ? dt.getDate() : "—"}</p>
          <p className="text-sm font-bold uppercase tracking-wide text-[#121212]/70">{dt ? DOW[dt.getDay()] : "Date TBD"}</p>
          {p.preferred_time && <p className="text-sm text-[#121212]/80 mt-1 inline-flex items-center gap-1"><Clock size={12} /> {p.preferred_time}</p>}
          {p.alternate_date && <p className="text-xs text-[#121212]/50 mt-1">Alt: {longDate(p.alternate_date)}</p>}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif font-bold uppercase tracking-tight text-2xl sm:text-3xl text-[#121212] leading-tight">{childHeadline(p)}</h3>
          <p className="text-[#121212]/80 mt-1">{p.package || "Package TBD"} · {p.num_guests || "?"} decorators</p>
          <p className="text-sm text-[#121212]/60 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            <span>{p.name}</span>{p.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {p.phone}</span>}
          </p>
          {addons.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {addons.map((a) => (
                <span key={a.key} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#B4472F] bg-[#F7E9E4] px-2.5 py-1 rounded"><a.Icon size={13} /> {a.label.replace(/ by .*/, "").split(" ").slice(0, 3).join(" ")}</span>
              ))}
            </div>
          )}
        </div>
        <div className="w-full lg:w-52 shrink-0 flex flex-col items-start lg:items-end gap-3">
          <span data-testid={`party-status-${p.id}`} className={`inline-flex px-3 py-1.5 text-xs font-bold uppercase tracking-[0.1em] ${meta.cls}`}>{meta.label}</span>
          {ATTENTION_KEYS.has(meta.key) && <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-[#E0218A]">Needs attention</span>}
          <span data-testid={`open-party-${p.id}`} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#121212] group-hover:text-[#E0218A]">
            {expanded ? <>Close <ChevronUp size={14} /></> : <>Open party <ChevronDown size={14} /></>}
          </span>
        </div>
      </div>

      {/* ── Expanded detail ── */}
      {expanded && (
        <div data-testid={`party-detail-${p.id}`} className="bg-[#F2E9D8] border-t border-[#121212]/10 p-6 md:p-10">
          <ProgressStepper p={p} />
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            {/* Left: details, add-ons, management */}
            <div className="xl:col-span-7 space-y-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#121212]/50 mb-3">Party details</p>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm text-[#121212]/80">
                  <div><span className="text-[#121212]/50">Customer</span><br />{p.name || "—"}</div>
                  <div><span className="text-[#121212]/50">Contact</span><br />{p.phone || "—"}{p.email ? <><br />{p.email}</> : ""}</div>
                  <div><span className="text-[#121212]/50">Package</span><br />{p.package || "—"}</div>
                  <div><span className="text-[#121212]/50">Decorating guests</span><br />{p.num_guests || "?"}</div>
                  {p.theme_colors && <div className="col-span-2"><span className="text-[#121212]/50">Theme / colors</span><br />{p.theme_colors}</div>}
                  {p.special_requests && <div className="col-span-2 italic">"{p.special_requests}"</div>}
                </div>
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#121212]/50 mb-3">Add-ons</p>
                {addons.length === 0 ? <p className="text-sm text-[#121212]/50">No add-ons selected</p> : (
                  <div className="space-y-2">
                    {addons.map((a) => (
                      <div key={a.key} className="flex items-center gap-3 text-sm"><a.Icon size={16} className="text-[#B4472F]" /> <span className="font-medium">{a.label}</span>{a.detail && <span className="text-[#121212]/50">— {a.detail}</span>}</div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#121212]/50 mb-3">Party management</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <button data-testid={`edit-party-${p.id}`} onClick={() => setEditing((v) => !v)} className={`${primaryBtn} bg-[#121212] text-white hover:bg-black`}>
                    {editing ? <><X size={14} /> Close editor</> : <><Pencil size={14} /> Edit party</>}
                  </button>
                  {canSendFD && (
                    <button data-testid={`send-final-details-${p.id}`} onClick={sendFD} disabled={sendingFD} className={`${primaryBtn} border-2 border-[#F5A623] text-[#a86c00] hover:bg-[#F5A623] hover:text-[#121212]`}>
                      <FileText size={14} /> {sendingFD ? "Sending…" : p.final_details_sent_at ? "Resend final details" : "Send final details"}
                    </button>
                  )}
                  {canRunSheet && (
                    <button data-testid={`print-runsheet-${p.id}`} onClick={() => openRunSheet(p)} className={`${primaryBtn} bg-[#C6FF33] text-[#121212] border-2 border-[#121212] hover:bg-[#121212] hover:text-[#C6FF33]`}>
                      <Printer size={14} /> Print run-sheet
                    </button>
                  )}
                  <div className="relative">
                    <button data-testid={`more-menu-${p.id}`} onClick={() => setShowMore((v) => !v)} className={`${primaryBtn} border-2 border-[#121212]/20 text-[#121212] hover:border-[#121212]`}>More {showMore ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
                    {showMore && (
                      <div className="absolute z-20 mt-2 w-56 bg-white border-2 border-[#121212] shadow-lg p-1 flex flex-col text-left">
                        {p.status !== "contacted" && p.status !== "confirmed" && <button data-testid={`party-contacted-${p.id}`} onClick={() => setInquiryStatus(p.id, "contacted")} className="px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold hover:bg-[#C6FF33] text-left">Mark contacted</button>}
                        {p.status !== "confirmed" && <button data-testid={`party-confirmed-${p.id}`} onClick={() => setInquiryStatus(p.id, "confirmed")} className="px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold hover:bg-[#F2E9D8] text-left">Mark deposit paid</button>}
                        {p.status === "confirmed" && <button data-testid={`party-unconfirm-${p.id}`} onClick={() => setInquiryStatus(p.id, "contacted")} className="px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold hover:bg-[#F2E9D8] text-left">Undo confirm</button>}
                        {log.length > 0 && <button data-testid={`log-party-${p.id}`} onClick={() => { setShowLog((v) => !v); setShowMore(false); }} className="px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold hover:bg-[#F2E9D8] text-left">Activity log ({log.length})</button>}
                        {p.status !== "closed" && <button data-testid={`party-closed-${p.id}`} onClick={() => setInquiryStatus(p.id, "closed")} className="px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#B4472F] hover:bg-[#F7E9E4] text-left">Close booking</button>}
                      </div>
                    )}
                  </div>
                </div>
                {p.final_details_submitted_at && <p data-testid={`fd-submitted-${p.id}`} className="text-[11px] text-emerald-700 font-bold mt-2">Final details form submitted {new Date(p.final_details_submitted_at).toLocaleDateString()}</p>}
                {!p.final_details_submitted_at && p.final_details_sent_at && <p data-testid={`fd-sent-${p.id}`} className="text-[11px] text-[#121212]/60 mt-2">Final details form sent {new Date(p.final_details_sent_at).toLocaleDateString()}</p>}
              </div>

              {editing && <EditForm p={p} editParty={editParty} onClose={() => setEditing(false)} />}

              {showLog && log.length > 0 && (
                <div data-testid={`activity-log-${p.id}`} className="pt-4 border-t border-[#121212]/10">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]/50 mb-2 inline-flex items-center gap-1.5"><History size={13} /> Activity log</p>
                  <ul className="space-y-2">
                    {log.map((entry, i) => (
                      <li key={i} className="text-xs text-[#121212]/60">
                        <span className="text-[#121212]/40">{new Date(entry.at).toLocaleString()}</span>
                        <ul className="list-disc pl-5 mt-0.5">{(entry.changes || []).map((c, j) => <li key={j}>{c}</li>)}</ul>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Right: consolidated Payment card */}
            <div className="xl:col-span-5">
              <div className="bg-white border border-[#121212]/10 p-6 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#121212]/50 mb-4">Payment</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-[#121212]/60">Deposit</span><span className="font-bold">${depAmt.toFixed(2)} · {p.deposit_paid ? <span className="text-emerald-700">Paid</span> : <span className="text-[#B4472F]">Unpaid</span>}</span></div>
                  {p.discount_type && <div className="flex justify-between"><span className="text-[#121212]/60">Discount</span><span className="font-bold">{p.discount_type === "percent" ? `${p.discount_value}%` : `$${Number(p.discount_value || 0).toFixed(2)}`}</span></div>}
                  {p.deposit_email_sent_at && !p.deposit_paid && <p data-testid={`deposit-requested-${p.id}`} className="text-[11px] text-[#E0218A] font-bold pt-1">Deposit link sent {new Date(p.deposit_email_sent_at).toLocaleDateString()} · awaiting payment</p>}
                </div>

                {showPayment && (
                  <FinalBalance p={p} getPartyBalance={getPartyBalance} createFinalPaymentLink={createFinalPaymentLink} markFinalPaid={markFinalPaid} reopenParty={reopenParty} />
                )}

                {(showDepositTools || (!p.deposit_paid && p.status !== "closed")) && (
                  <div className="mt-4 pt-4 border-t border-[#121212]/10">
                    <button data-testid={`payment-tools-${p.id}`} onClick={() => setShowPayTools((v) => !v)} className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]/70 inline-flex items-center gap-1.5"><Link2 size={13} /> Payment tools {showPayTools ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</button>
                    {showPayTools && (
                      <div className="mt-3">
                        <p className="text-[11px] text-[#121212]/50 mb-2">Request deposit via Stripe. When paid, the party auto-confirms and lands on the calendar.</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex items-center border-2 border-[#121212]"><span className="px-2 text-stone-500">$</span><input data-testid={`deposit-amount-${p.id}`} type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-20 px-2 py-1.5 focus:outline-none" /></div>
                          <button data-testid={`gen-deposit-${p.id}`} onClick={() => generate(false)} disabled={busy} className="px-3 py-1.5 text-xs tracking-[0.1em] uppercase font-bold bg-[#E0218A] text-white border-2 border-[#E0218A] hover:opacity-90 disabled:opacity-50">{busy ? "…" : link ? "Regenerate" : "Generate link"}</button>
                          <button data-testid={`email-deposit-${p.id}`} onClick={() => generate(true)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.1em] uppercase font-bold bg-[#121212] text-[#F2E9D8] border-2 border-[#121212] hover:bg-black disabled:opacity-50"><Mail size={13} /> Email</button>
                        </div>
                        {link && (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <a data-testid={`deposit-link-${p.id}`} href={link} target="_blank" rel="noreferrer" className="text-sm underline break-all max-w-full">{link}</a>
                            <button data-testid={`copy-deposit-${p.id}`} onClick={copy} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold border border-[#121212] hover:bg-[#121212] hover:text-white">{copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function matchDate(p, filter) {
  const dt = parseDate(p.desired_date);
  if (filter === "upcoming") { if (!dt) return true; const t = new Date(); t.setHours(0, 0, 0, 0); return dt >= t; }
  if (filter === "past") { if (!dt) return false; const t = new Date(); t.setHours(0, 0, 0, 0); return dt < t; }
  if (filter === "week") {
    if (!dt) return false; const now = new Date(); now.setHours(0, 0, 0, 0);
    const mon = new Date(now); mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return dt >= mon && dt <= sun;
  }
  if (filter === "month") { if (!dt) return false; const n = new Date(); return dt.getFullYear() === n.getFullYear() && dt.getMonth() === n.getMonth(); }
  return true;
}
function matchStatus(p, filter) {
  if (filter === "all") return true;
  const k = deriveStatus(p).key;
  if (filter === "attention") return ATTENTION_KEYS.has(k);
  if (filter === "ready") return k === "deposit_paid" || k === "paid_full";
  return k === filter;
}
function Chip({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${active ? "bg-[#121212] text-white" : "bg-transparent text-[#121212] border border-[#121212]/20 hover:bg-[#C6FF33] hover:border-[#C6FF33]"}`}>
      {children}
    </button>
  );
}

export default function AdminParties({ ctx }) {
  const { partyInquiries, setInquiryStatus, createDepositLink, editParty, sendFinalDetails, getPartyBalance, createFinalPaymentLink, markFinalPaid, reopenParty, totals } = ctx;
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState([]);
  const [archLimit, setArchLimit] = useState(50);
  const [dateFilter, setDateFilter] = useState("upcoming");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadArchived = async (limit = 50) => {
    try {
      const { data } = await api.get(`/admin/party-inquiries?archived=true&limit=${limit}`);
      setArchived(data || []); setArchLimit(limit);
    } catch (e) { toast.error("Couldn't load archived parties"); }
  };
  const switchTo = (arch) => { setShowArchived(arch); if (arch) { setDateFilter("past"); loadArchived(50); } else { setDateFilter("upcoming"); } };

  const activeCount = totals?.parties ?? partyInquiries.length;
  const archivedCount = totals?.parties_archived ?? 0;
  const source = showArchived ? archived : partyInquiries;
  const list = source
    .filter((p) => matchDate(p, dateFilter) && matchStatus(p, statusFilter))
    .sort((a, b) => {
      const da = parseDate(a.desired_date), db_ = parseDate(b.desired_date);
      if (!da && !db_) return 0;
      if (!da) return 1;
      if (!db_) return -1;
      return dateFilter === "past" ? db_ - da : da - db_;
    });
  const cardProps = { setInquiryStatus, createDepositLink, editParty, sendFinalDetails, getPartyBalance, createFinalPaymentLink, markFinalPaid, reopenParty };

  const DATE_FILTERS = [["upcoming", "Upcoming"], ["week", "This Week"], ["month", "This Month"], ["past", "Past"]];
  const STATUS_FILTERS = [["all", "All"], ["attention", "Needs Attention"], ["deposit_needed", "Deposit Needed"], ["final_details", "Final Details"], ["final_due", "Final Payment Due"], ["ready", "Ready"]];

  return (
    <div className="mt-10 space-y-6" data-testid="admin-parties">
      <div className="inline-flex border-2 border-[#121212] rounded-lg overflow-hidden" data-testid="parties-view-toggle">
        <button data-testid="parties-tab-active" onClick={() => switchTo(false)} className={`px-5 py-2 text-xs tracking-[0.15em] uppercase font-bold transition-colors ${!showArchived ? "bg-[#121212] text-[#C6FF33]" : "bg-white text-[#121212] hover:bg-stone-100"}`}>Active ({activeCount})</button>
        <button data-testid="parties-tab-archived" onClick={() => switchTo(true)} className={`px-5 py-2 text-xs tracking-[0.15em] uppercase font-bold border-l-2 border-[#121212] transition-colors ${showArchived ? "bg-[#121212] text-[#C6FF33]" : "bg-white text-[#121212] hover:bg-stone-100"}`}>Archived ({archivedCount})</button>
      </div>

      <div className="space-y-3 pb-2 border-b border-[#121212]/10" data-testid="parties-filters">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#121212]/40 mr-1">When</span>
          {DATE_FILTERS.map(([v, l]) => <Chip key={v} testid={`filter-date-${v}`} active={dateFilter === v} onClick={() => setDateFilter(v)}>{l}</Chip>)}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#121212]/40 mr-1">Status</span>
          {STATUS_FILTERS.map(([v, l]) => <Chip key={v} testid={`filter-status-${v}`} active={statusFilter === v} onClick={() => setStatusFilter(v)}>{l}</Chip>)}
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-[#121212]/60 py-8 text-center">No parties match these filters.</p>
      ) : (
        <div className="border-2 border-[#121212] bg-white overflow-hidden rounded-lg">
          {list.map((p) => <PartyCard key={p.id} p={p} {...cardProps} />)}
        </div>
      )}

      {showArchived
        ? (archived.length >= archLimit && (
            <div className="pt-2 text-center space-y-2">
              {archivedCount > 0 && <p className="text-xs tracking-[0.15em] uppercase text-stone-400">Showing {archived.length} of {archivedCount}</p>}
              <button data-testid="load-more-archived" onClick={() => loadArchived(archLimit + 50)} className="inline-flex items-center gap-2 px-6 py-2.5 border-2 border-[#121212] text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#121212] hover:text-[#C6FF33] transition-colors rounded-lg">Load older parties</button>
            </div>
          ))
        : <LoadMore ctx={ctx} pkey="parties" label="Load older parties" />}
    </div>
  );
}
